import {NavigationBarItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {
  BadgeListSmall,
  COLORS,
  FONT_SIZES,
  Icon,
  IconName,
  UnstyledLinkLinaria,
  Z_INDEXES,
  useCallbackDelayed,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useNavigationItemEvents} from '../hooks/useNavigationItemEvents';

interface CategoryNavigationItemProps
  extends Omit<NavigationBarItem, 'flyout'> {
  isActive: boolean;
  onEnterNavigationItem: () => void;
  itemHasContent: boolean;
}

export const CategoryNavigationItem: React.FC<
  CategoryNavigationItemProps & {
    icon?: {
      color: string;
      ['data-testid']: string;
      asset: IconName;
    };
  }
> = ({
  badges,
  isActive,
  itemHasContent,
  label,
  onEnterNavigationItem,
  textColor,
  url: linkTarget,
  icon,
}) => {
  const {
    delayedCallback: handleMouseEnterWithDelay,
    cancelCallback: handleMouseLeave,
  } = useCallbackDelayed(onEnterNavigationItem, 50);
  const shouldBeClickable = linkTarget.url.length > 0;
  const {
    handleMouseEnterNavItem,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    onItemClick,
  } = useNavigationItemEvents(
    linkTarget,
    handleMouseEnterWithDelay,
    itemHasContent,
  );

  const LinkComp = shouldBeClickable ? UnstyledLinkLinaria : BasicNavItem;

  return (
    <NavigationItem
      data-show-arrow={isActive && itemHasContent}
      data-testid={`Header_navigation_list_item_${label}`}
      onMouseEnter={handleMouseEnterNavItem}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
    >
      <LinkComp
        data-testid={`LinkToUrl_${label}`}
        onClick={onItemClick}
        to={linkTarget.url}
        aria-label={undefined}
      >
        <LabelWrapper
          data-testid={`Label_${label}`}
          $isActive={isActive}
          $textColor={textColor}
        >
          {label}
          {icon && (
            <StyledSaleIcon
              size="XS"
              asset={icon.asset}
              fill={icon.color}
              data-testid={icon['data-testid']}
            />
          )}
        </LabelWrapper>
        {badges.length > 0 && (
          <BadgesWrapper>
            <BadgeListSmall
              entries={badges.map(badge => ({
                kind: 'GENERIC',
                untranslatedLabel: '',
                ...badge,
              }))}
              disableRoundedCorners={false}
            />
          </BadgesWrapper>
        )}
      </LinkComp>
    </NavigationItem>
  );
};

const BasicNavItem = styled.span`
  cursor: default;
`;

const NavigationItem = styled.li`
  position: relative;
  padding-top: 13px;
  padding-bottom: 13px;
  padding-left: clamp(1px, 0.65vw, 11px);
  padding-right: clamp(1px, 0.65vw, 11px);
  font-size: clamp(${FONT_SIZES.px11}, 1vw, ${FONT_SIZES.px14});
  line-height: 1.25em;
  height: 100%;

  &:first-child {
    padding-left: 0;
  }

  &[data-show-arrow='true']::after {
    display: block;
    background: ${COLORS.White};
    bottom: -1px;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
    content: '';
    height: 10px;
    left: 50%;
    position: absolute;
    transform: translateX(-50%) translateY(50%) rotate(45deg);
    width: 10px;
    z-index: ${Z_INDEXES.DESKTOP_NAVIGATION_FLYOUT};
  }

  ${UnstyledLinkLinaria} {
    display: flex;
  }
`;

const LabelWrapper = styled.div<{$isActive: boolean; $textColor?: string}>`
  padding: 5px 4px;
  border-radius: 2px;
  position: relative;

  color: ${({$textColor}) => $textColor || 'inherit'};
  background: ${({$isActive}) => ($isActive ? COLORS.Grey4 : 'transparent')};
  display: flex;
  align-items: center;
`;

const BadgesWrapper = styled.div`
  position: relative;
  right: 0;
  bottom: 5px;
  margin-left: 2px;
`;

const StyledSaleIcon = styled(Icon)`
  margin-left: 4px;
`;
